import {all, call, fork, put, select, takeLatest, takeEvery} from 'redux-saga/effects'
import {
  addLayoutFailure,
  addLayoutSuccess,
  deleteLayoutFailure,
  deleteLayoutSuccess,
  loadLayoutPanelFailure,
  loadLayoutPanelSuccess,
  loadLayoutsFailure,
  loadLayoutsSuccess,
  updateLayoutFailure,
  updateLayoutSuccess,
} from 'actions/layouts'
import {generalError} from 'actions/index'
import types from 'actions/types'
import {
  isOrdersLayoutType,
  updateSalesAnalysisDataSourceFilters,
  updateSalesAnalysisDataSourceIsOverriding,
} from 'helpers/sales'
import {
  getDefaultLayoutIdForDataSource,
  getFiltersFromLayout,
  removeTemporaryIdsForLayout,
} from 'helpers/layouts'
import {snackbarMessage} from 'actions/snackbar'
import {iconNames} from 'components/icon'
import {getSalesAnalysisDataSource, getSalesAnalysisDataSourceLayoutPanels} from 'selectors/salesContext'
import {updateSalesAnalysisDataSourceLayoutPanels} from 'actions/sales'
import uuid from "helpers/uuid"

const layoutFields = {
  layoutFilters: true,
  layoutGroups: true,
  layoutPanels: true,
  default: true,
  owner: true,
}

export default (api) => {
  function* loadLayouts() {
    try {
      const params = {
        isGlobal: false,
      }
      const response = yield call(api.layouts.get, params, layoutFields)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const stats = data.result
            yield put(loadLayoutsSuccess(stats))
          },
          function* onFail(key, message, code) {
            yield put(loadLayoutsFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadLayoutPanel({index, dataSource, params}) {
    try {
      const apiCall = isOrdersLayoutType(dataSource)
          ? api.quote.summary
          : api.invoice.summary
      const response = yield call(apiCall, params)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const result = data.result
            yield put(loadLayoutPanelSuccess(index, result))
          },
          function* onFail(key, message, code) {
            yield put(loadLayoutPanelFailure(index, message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addLayout({layout}) {
    try {
      const params = {layout: removeTemporaryIdsForLayout(layout)}
      const response = yield call(api.layouts.add, params, layoutFields)

      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const stats = data.result[0]
            const filters = getFiltersFromLayout({
              layout: stats,
              dataSource: stats.type,
            })
            yield put(addLayoutSuccess(stats))
            yield put(updateSalesAnalysisDataSourceFilters(
                {
                  ...filters,
                  layoutId: stats.id,
                  isOverriding: false,
                },
                stats.type))
            yield put(snackbarMessage(`Layout has been added`, iconNames.tick, true))
          },
          function* onFail(key, message, code) {
            yield put(addLayoutFailure(message, code))
          })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateLayout({layout}) {
    try {
      if (layout) {
        const params = {layout: removeTemporaryIdsForLayout(layout)}
        const response = yield call(api.layouts.update, params, layoutFields)

        yield api.handleResponse(
            response,
            function* onOk(key, data) {
              const stats = data.result[0]
              yield put(updateLayoutSuccess(stats))
              yield put(updateSalesAnalysisDataSourceIsOverriding(
                  false,
                  stats.type))
              yield put(snackbarMessage(`Layout has been updated`, iconNames.tick, true))
            },
            function* onFail(key, message, code) {
              yield put(updateLayoutFailure(message, code))
            })
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteLayout({id, dataSource, isPrivate}) {
    try {
      const params = {
        isGlobal: false,
        id,
        isPrivate,
      }

      if (id) {
        const response = yield call(api.layouts.delete, params, layoutFields, id)

        yield api.handleResponse(
            response,
            function* onOk(key, data) {
              const stats = data.result
              yield put(deleteLayoutSuccess(stats))
              yield put(updateSalesAnalysisDataSourceFilters(
                  {
                    layoutId: getDefaultLayoutIdForDataSource(dataSource),
                    isOverriding: false,
                  },
                  dataSource))
              yield put(snackbarMessage(`Layout has been deleted`, iconNames.tick, true))

            },
            function* onFail(key, message, code) {
              yield put(deleteLayoutFailure(message, code))
            })
      }
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addLayoutPanel({chart}) {
    const layoutPanels = yield select(getSalesAnalysisDataSourceLayoutPanels)
    const dataSource = yield select(getSalesAnalysisDataSource)
    const updatedLayoutPanels = [...layoutPanels]
    const uniqueId = uuid()
    updatedLayoutPanels.push({
      ...chart,
      id: uniqueId,
      sortOrder: layoutPanels.length,
    })
    yield put(updateSalesAnalysisDataSourceLayoutPanels(updatedLayoutPanels))
    yield put(updateSalesAnalysisDataSourceIsOverriding(true, dataSource))
  }

  function* updateLayoutPanel({index, chart}) {
    const layoutPanels = yield select(getSalesAnalysisDataSourceLayoutPanels)
    const dataSource = yield select(getSalesAnalysisDataSource)
    const updatedLayoutPanels = [...layoutPanels]
    updatedLayoutPanels[index] = chart
    yield put(updateSalesAnalysisDataSourceLayoutPanels(updatedLayoutPanels))
    yield put(updateSalesAnalysisDataSourceIsOverriding(true, dataSource))
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.LOAD_LAYOUTS_ATTEMPT, loadLayouts),
      takeEvery(types.LOAD_LAYOUT_PANEL_ATTEMPT, loadLayoutPanel),
      takeLatest(types.SAVE_LAYOUT_ATTEMPT, addLayout),
      takeLatest(types.UPDATE_LAYOUT_ATTEMPT, updateLayout),
      takeLatest(types.DELETE_LAYOUT_ATTEMPT, deleteLayout),
      takeLatest(types.LAYOUT_PANEL_ADD_ATTEMPT, addLayoutPanel),
      takeLatest(types.LAYOUT_PANEL_UPDATE_ATTEMPT, updateLayoutPanel),
    ])
  }

  function* watcher() {
    yield fork(myWatcher)
  }

  return {
    watcher,
  }
}
